import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { adminMothersApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Heart, Mail, Phone, Calendar, Baby,
  Activity, Film, Camera,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return iso; }
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const genderLabel = (g) => g === 'female' ? 'Kız' : g === 'male' ? 'Erkek' : g === 'unknown' ? 'Sürpriz' : '—';

export const MotherDetailPage = () => {
  const { motherId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!motherId) return;
    adminMothersApi.get(motherId)
      .then(setData)
      .catch(() => toast.error('Anne bilgileri yüklenemedi'))
      .finally(() => setLoading(false));
  }, [motherId]);

  if (loading) {
    return <DashboardLayout title="Anne Detayı" subtitle="Yükleniyor..."><div className="text-center p-8 text-slate-400">Yükleniyor...</div></DashboardLayout>;
  }
  if (!data?.mother) {
    return (
      <DashboardLayout title="Anne Detayı" subtitle="Bulunamadı">
        <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-slate-400">Anne bulunamadı</CardContent></Card>
      </DashboardLayout>
    );
  }

  const { mother, generations } = data;
  const fullName = mother.name || `${mother.first_name || ''} ${mother.last_name || ''}`.trim();

  return (
    <DashboardLayout title={fullName || 'Anne Detayı'} subtitle={mother.email}>
      <div className="space-y-6" data-testid="admin-mother-detail-page">
        <Link to="/superadmin/mothers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#FB7185] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Annelere geri dön
        </Link>

        {/* Profile */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="py-4 px-6 bg-gradient-to-r from-[#FB7185] to-[#F43F5E]">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Heart className="w-4 h-4" fill="currentColor" /> Profil
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow icon={<Heart className="w-4 h-4" />} label="İsim" value={fullName} />
              <InfoRow icon={<Mail className="w-4 h-4" />} label="E-posta" value={mother.email} />
              <InfoRow icon={<Phone className="w-4 h-4" />} label="Telefon" value={mother.phone || '—'} />
              <InfoRow icon={<Calendar className="w-4 h-4" />} label="Kayıt Tarihi" value={formatDate(mother.created_at)} />
              <InfoRow icon={<Baby className="w-4 h-4" />} label="Gebelik Haftası" value={mother.pregnancy_week ? `${mother.pregnancy_week}. hafta` : '—'} />
              <InfoRow icon={<Baby className="w-4 h-4" />} label="Bebek Cinsiyeti" value={genderLabel(mother.baby_gender)} />
              <InfoRow icon={<Calendar className="w-4 h-4" />} label="Tahmini Doğum" value={mother.due_date ? formatDate(mother.due_date) : '—'} />
              <div className="flex items-start gap-3 py-2">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-500">
                  <Activity className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Durum</p>
                  <div className="flex gap-1 mt-1">
                    <Badge variant={mother.is_active ? 'default' : 'secondary'}>{mother.is_active ? 'Aktif' : 'Pasif'}</Badge>
                    {!mother.onboarding_completed && <Badge variant="outline">Onboarding eksik</Badge>}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generations */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="py-4 px-6 border-b border-slate-100">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Film className="w-4 h-4 text-[#FB7185]" /> Üretim Geçmişi ({generations?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(!generations || generations.length === 0) ? (
              <div className="p-8 text-center text-slate-400 text-sm" data-testid="admin-mother-generations-empty">
                Henüz üretim yok — üretim Faz 2'de aktif olacak
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-mother-generations-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-6 py-3 font-medium">Tarih</th>
                      <th className="text-left px-6 py-3 font-medium">Tür</th>
                      <th className="text-left px-6 py-3 font-medium">Durum</th>
                      <th className="text-left px-6 py-3 font-medium">Scan ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {generations.map((g) => (
                      <tr key={g.id} className="hover:bg-slate-50">
                        <td className="px-6 py-3 text-slate-600">{formatDateTime(g.created_at)}</td>
                        <td className="px-6 py-3">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700">
                            {g.scan_type === 'video' ? <Film className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
                            {g.scan_type || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <Badge variant={g.status === 'generated' ? 'default' : g.status === 'failed' ? 'destructive' : 'secondary'}>
                            {g.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-3 font-mono text-xs text-slate-500">{g.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-500">{icon}</div>
    <div className="min-w-0"><p className="text-xs text-slate-500">{label}</p><p className="text-sm text-slate-800 font-medium truncate">{value}</p></div>
  </div>
);

export default MotherDetailPage;
